import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, Disclaimer } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lock, Sparkles, Headphones, Video, FileText, Type, Dumbbell, ExternalLink, ShoppingCart } from "lucide-react";

const FORMATS = { audio: Headphones, video: Video, pdf: FileText, texto: Type, exercicio: Dumbbell };

export default function Library() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [detail, setDetail] = useState(null);
  const isVitrine = !!user?.self_registered;

  useEffect(() => {
    api.get("/content").then(({ data }) => setList(data));
    api.get("/categories").then(({ data }) => setCats(data));
  }, []);

  const payContent = async (c) => {
    try {
      const { data } = await api.post("/payments/content", { content_id: c.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const open = async (c) => {
    if (c.locked) {
      if (isVitrine) { payContent(c); return; }
      toast.info("Conteúdo bloqueado. Fale com seu hipnoterapeuta para desbloquear.");
      return;
    }
    try { const { data } = await api.get(`/content/${c.id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const categories = ["Todos", ...cats.map((c) => c.name)];
  const filtered = (cat) => cat === "Todos" ? list : list.filter((c) => c.category === cat);

  const ContentCard = ({ c }) => {
    const Icon = FORMATS[c.format] || Type;
    return (
      <Card className={`p-5 flex flex-col ${c.locked ? "opacity-95" : "hover:-translate-y-1 hover:shadow-lg transition-all cursor-pointer"}`}
        onClick={() => open(c)} data-testid={`lib-${c.id}`}>
        <div className="flex items-start justify-between">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.locked ? "bg-secondary text-muted-foreground" : "bg-primary/10 text-primary"}`}>
            {c.locked ? <Lock className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
          </div>
          <div className="flex gap-1">
            {c.recommended && <Badge className="bg-accent/15 text-accent"><Sparkles className="w-3 h-3 mr-1" />Recomendado</Badge>}
            {c.is_premium && <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Premium</Badge>}
          </div>
        </div>
        <h3 className="font-medium mt-3">{c.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mt-1 flex-1">{c.description}</p>
        <div className="flex items-center justify-between mt-3">
          <Badge variant="secondary">{c.category}</Badge>
          {c.locked
            ? <Button size="sm" className="rounded-full" data-testid={`unlock-cta-${c.id}`}>
                {isVitrine ? <><ShoppingCart className="w-3.5 h-3.5 mr-1" />Comprar R$ {Number(c.price || 0).toFixed(2)}</> : <><Lock className="w-3.5 h-3.5 mr-1" />Desbloquear</>}
              </Button>
            : <span className="text-xs text-primary font-medium">Abrir →</span>}
        </div>
      </Card>
    );
  };

  return (
    <div>
      <PageHeader title={isVitrine ? "Vitrine de Conteúdos" : "Conteúdos"}
        subtitle={isVitrine ? "Conteúdos gratuitos liberados. Compre conteúdos premium avulsos quando quiser." : "Biblioteca de conteúdos para o seu bem-estar"} />
      <Tabs defaultValue="Todos">
        <TabsList className="flex-wrap h-auto">
          {categories.slice(0, 6).map((c) => <TabsTrigger key={c} value={c} data-testid={`cat-${c}`}>{c}</TabsTrigger>)}
        </TabsList>
        {categories.slice(0, 6).map((cat) => (
          <TabsContent key={cat} value={cat} className="mt-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered(cat).map((c) => <ContentCard key={c.id} c={c} />)}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Card className="mt-6 p-5 bg-primary/5 border-primary/20">
        <p className="text-sm">{isVitrine
          ? "Cada compra desbloqueia o conteúdo permanentemente na sua biblioteca. Pagamento seguro via cartão ou PIX."
          : "Este conteúdo é complementar ao seu acompanhamento. Para uma orientação personalizada, converse com seu hipnoterapeuta."}</p>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.title}</DialogTitle></DialogHeader>
              <Badge variant="secondary" className="w-fit">{detail.category}</Badge>
              <p className="text-muted-foreground text-sm">{detail.description}</p>
              {detail.body_text && <p className="text-sm whitespace-pre-wrap">{detail.body_text}</p>}
              {detail.url && <a href={detail.url} target="_blank" rel="noreferrer"><Button className="rounded-full"><ExternalLink className="w-4 h-4 mr-1" />Abrir conteúdo</Button></a>}
            </>
          )}
        </DialogContent>
      </Dialog>
      <Disclaimer />
    </div>
  );
}
